import type { FC, KeyboardEvent, Ref, WheelEvent } from 'react';
import { Suspense, lazy } from 'react';

import { TextField } from '@mui/material';

import { InputPropertyOwnership } from 'components/inputs/InputPropertyOwnership/InputPropertyOwnership';
import { InputPropertyPicklist } from 'components/inputs/InputPropertyPicklist/InputPropertyPicklist';
import type { PropertyValueChangeHandler } from 'hooks/usePropertyValueEditor';
import { EPropertyEditTypes } from 'state/selectedTemplate/selectedTemplateStore';
import type { Nullable } from 'utils/values';

const InputPropertyDate = lazy(async () => ({
  default: (
    await import(/* webpackChunkName: "input-property-date" */ 'components/inputs/InputPropertyDate/InputPropertyDate')
  ).InputPropertyDate
}));

/** Rows of a multiline text property before it starts scrolling. */
const TEXT_MAX_ROWS = 12;

const NUMBER_SLOT_PROPS = {
  htmlInput: {
    // `type='number'` alone does not get a numeric keypad on every touch device. 'decimal' rather
    // than 'numeric' because the same field edits Rational properties, and a digits-only keypad has
    // no decimal separator or minus sign to offer them.
    inputMode: 'decimal',
    // Without this the step defaults to 1 and the browser treats every decimal on a Rational
    // property as a step mismatch. Whole numbers are `validatePropertyValue`'s job instead.
    step: 'any',
    /**
     * A focused `type='number'` steps its value on scroll, and the property list this field sits in
     * scrolls — so a scroll past a focused field would silently change the number, and the blur
     * after it would save that. `preventDefault` is not an option: React 18 registers `wheel`
     * passively. Blurring is, and it reads as the same commit-and-leave a click elsewhere does,
     * because the browser only steps a field that still has focus when the default action runs.
     */
    onWheel: (event: WheelEvent<HTMLInputElement>) => event.currentTarget.blur()
  }
} as const;

/** Keystrokes MUI's number input would accept but the backend cannot store. */
const REJECTED_NUMBER_KEYS = ['+', '~'];

export interface PropertyValueInputProps {
  editType: EPropertyEditTypes;
  label: string;
  /** Always a string — the controlled MUI inputs must never receive null. */
  value: string;
  onValueChange: PropertyValueChangeHandler;
  onBlur: () => void;
  onFocus: () => void;
  onKeyDown: (event: KeyboardEvent<HTMLElement>) => void;
  focused?: boolean;
  disabled?: boolean;
  /** Validation message to show under the input, or null when the value is valid. */
  errorMessage?: Nullable<string>;
  inputRef?: Ref<HTMLInputElement>;
  /** Required by the picklist and ownership inputs, which resolve their own options. */
  propertyId: string;
  templateNodeId: string;
}

/**
 * Renders the input a property value is edited with. The single dispatch point for both node-details
 * panels — it holds no state of its own, so the edit state machine (`usePropertyValueEditor`) and the
 * data layer stay with the caller.
 */
export const PropertyValueInput: FC<PropertyValueInputProps> = ({
  editType,
  label,
  value,
  onValueChange,
  onBlur,
  onFocus,
  onKeyDown,
  focused,
  disabled,
  errorMessage,
  inputRef,
  propertyId,
  templateNodeId
}) => {
  const error = Boolean(errorMessage);
  const labelShrunk = focused || Boolean(value) || error;
  const labelSlotProps = { inputLabel: { shrink: labelShrunk } };

  // Shared by every input except Ownership, which has no focus/keyboard/validation UI of its own.
  const commonProps = { label, value, onBlur, disabled };
  // The value-shaped inputs take the element ref as `ref`; MUI's TextField takes it as `inputRef`.
  const fieldProps = {
    ...commonProps,
    onFocus,
    onKeyDown,
    focused,
    error,
    helperText: errorMessage,
    slotProps: labelSlotProps
  };

  switch (editType) {
    case EPropertyEditTypes.Date:
      return (
        <Suspense fallback={<TextField label={label} value={value} multiline disabled />}>
          <InputPropertyDate {...fieldProps} ref={inputRef} onValueChange={onValueChange} />
        </Suspense>
      );

    // The browser sanitises a `type='number'` value that does not parse to a finite float before it
    // ever reaches onChange, so the field cannot overflow past double range on its own — but it also
    // cannot report `123-123` as anything other than empty, which is why blur below hands the
    // editor the element's own verdict on what it is holding.
    case EPropertyEditTypes.Number:
      return (
        <TextField
          {...fieldProps}
          inputRef={inputRef}
          type='number'
          slotProps={{ ...NUMBER_SLOT_PROPS, ...labelSlotProps, input: { notched: labelShrunk } }}
          onChange={(event) => onValueChange(event.target.value)}
          // Deliberately only on blur, not on every keystroke: the halfway state of typing any
          // decimal ('1.') is unparsable too, and flagging that mid-word would put an error under
          // the commonest edit there is. Blur is also the point the message is about — it is what
          // would have saved.
          onBlur={(event) => {
            onValueChange(event.target.value, { badInput: event.target.validity.badInput });
            onBlur();
          }}
          onKeyDown={(event) => {
            if (REJECTED_NUMBER_KEYS.includes(event.key)) {
              event.preventDefault();
            }
            onKeyDown(event);
          }}
        />
      );

    case EPropertyEditTypes.Ownership:
      return (
        <InputPropertyOwnership
          {...commonProps}
          templateNodeId={templateNodeId}
          propertyId={propertyId}
          onValueChange={onValueChange}
        />
      );

    case EPropertyEditTypes.Picklist:
      return (
        <InputPropertyPicklist {...fieldProps} ref={inputRef} propertyId={propertyId} onValueChange={onValueChange} />
      );

    case EPropertyEditTypes.Text:
      return (
        <TextField
          {...fieldProps}
          inputRef={inputRef}
          multiline
          maxRows={TEXT_MAX_ROWS}
          onChange={(event) => onValueChange(event.target.value)}
        />
      );

    // An Auto-ID is assigned by the backend and an unrecognised setting type has no editor, so both
    // show the value as read-only text rather than nothing at all.
    case EPropertyEditTypes.AutoId:
    case EPropertyEditTypes.Unknown:
      return <TextField label={label} value={value} multiline disabled />;
  }
};
